import * as React from 'react';
import { useRef } from 'react';
import { AreaKey } from '../rows-data/types';
import EventEmitter from 'eventemitter3';
import { getFinalTableKey } from './utils';
import {
  FinalTableEvent,
  FinalTableEventType,
  GroupStatus,
} from './FinalTableProps';

const eventEmitter = new EventEmitter();

export type FinalTableDataMapItem = {
  /**
   * Klíč pro data.
   */
  dataKey: string;
  /**
   * Reálný počet řádků v konkrétním zobrazení - tedy např. datové řádky a skupiny - to co se zobrazuje jako řádek.
   */
  rowsCount?: number;
  /**
   * Reálný počet "datových" řádků tabulky - pokud mám např. seskupeno, tak zobrazuji tři skupiny (rowsCount = 3) ale reálný počet datových řádků když skupiny zobrazím je např. 10 - tedy skupiny se v tomto počtu nezahrnují.
   */
  dataRowsCount?: number;
  depth: number[];
  groupStatus: GroupStatus[];
  group: boolean[];
  ids: any[];
};

const initialDataMapItemValue: FinalTableDataMapItem = {
  dataKey: '',
  depth: [],
  groupStatus: [],
  group: [],
  ids: [],
};

export type DataMap = {
  [key: string]: FinalTableDataMapItem;
};

type FinalTableEventFc = (event: FinalTableEvent) => void;

export type FinalTableContextData = {
  initByProvider: boolean;
  eventEmitter?: EventEmitter;
  registerEvents: (filterKey: AreaKey, efc: FinalTableEventFc) => void;
  unregisterEvents: (filterKey: AreaKey) => void;
  getItem: (key: string) => FinalTableDataMapItem;
  setItem: (key: string, item: FinalTableDataMapItem) => void;
};

export const FinalTableDataContext = React.createContext<FinalTableContextData>(
  {
    getItem: () => initialDataMapItemValue,
    setItem: () => {},
    registerEvents: () => {},
    unregisterEvents: () => {},
    initByProvider: false,
  }
);

export const FinalTableDataProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const dataMapRef = useRef<DataMap>({});

  const getItem = (key: string): FinalTableDataMapItem => {
    let val = dataMapRef.current[key];
    if (!val) {
      val = { ...initialDataMapItemValue };
      setItem(key, val);
    }
    return val;
  };

  const setItem = (key: string, item: FinalTableDataMapItem) => {
    let newMap = {
      ...dataMapRef.current,
      [key]: item,
    };
    dataMapRef.current = newMap;
  };

  const registerEvents = (filterKey: AreaKey, efc: FinalTableEventFc) => {
    eventEmitter.addListener(getFinalTableKey(filterKey), efc);
  };

  const unregisterEvents = (filterKey: AreaKey) => {
    eventEmitter.removeListener(getFinalTableKey(filterKey));
  };

  const handleAny = (cacheKey: any, ...args: any[]) => {
    if (!cacheKey || typeof cacheKey !== 'string') return;
    if (args.length === 0) return;
    const event: FinalTableEvent = args[0];
    if (!event || !event.type) return;

    switch (event.type) {
      case FinalTableEventType.REFETCH:
        let globalData = getItem(cacheKey);
        if (globalData) {
          globalData.dataKey = '';
          globalData.ids = [];
          globalData.group = [];
          globalData.groupStatus = [];
          globalData.depth = [];
          globalData.rowsCount = undefined;
          globalData.dataRowsCount = undefined;
        }
        break;
    }
  };

  const originalEmit = eventEmitter.emit;
  eventEmitter.emit = function (eventName: any, ...args: any[]) {
    handleAny(eventName, ...args);
    return originalEmit.call(this, eventName, ...args);
  };

  return (
    <FinalTableDataContext.Provider
      value={{
        initByProvider: true,
        eventEmitter,
        registerEvents,
        unregisterEvents,
        getItem,
        setItem,
      }}
    >
      {children}
    </FinalTableDataContext.Provider>
  );
};
